import React, { useEffect, useState } from 'react';
import { compose, withHandlers } from 'recompose';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Grid } from '@material-ui/core';
import { RenderControl, RenderElement, RenderDelay } from 'components/RenderHelpers';
import { bindLogger } from 'helpers/BindLogger';
import {
  CanUserViewCountries,
  CanUserViewParticipation,
  CanUserEditParticipation,
  CanUserEditRestrictions,
  CanUserEditCountries
} from 'appconstants/Permission';
import {
  PARTICIPATION_ACTIONS,
  getParticipation,
  getChampions,
  getCountry,
  saveParticipation,
  saveRestriction,
  setParticipantsSync
} from 'actions/protocols/participation';
import {
  createLoadingSelector,
  createErrorMessageSelector,
  ProtocolIdSelector,
  ProtocolNumSelector,
  lSelectorGetProtDetails,
  eSelectorGetProtDetails
} from 'selectors';
import { withAppContext } from '@ctsu/loader';
import _ from 'lodash';
import atoms from 'ctsutheme/components/atoms';
import {
  RestrictionFor, StudyChampions, SiteRegistrationTest, Participation, ParticipationCountries
} from './components';

const {
  Typography
} = atoms;

const loadingCountriesSelector = createLoadingSelector([
  PARTICIPATION_ACTIONS.GET_COUNTRIES
]);

const errorCountriesSelector = createErrorMessageSelector([
  PARTICIPATION_ACTIONS.GET_COUNTRIES
]);

const loadingParticipationSelector = createLoadingSelector([
  PARTICIPATION_ACTIONS.GET_PARTICIPATION
]);
const errorParticipationSelector = createErrorMessageSelector([
  PARTICIPATION_ACTIONS.GET_PARTICIPATION
]);

const loadingChampSelector = createLoadingSelector([
  PARTICIPATION_ACTIONS.GET_CHAMPIONS
]);


const errorChampSelector = createErrorMessageSelector([
  PARTICIPATION_ACTIONS.GET_CHAMPIONS
]);


const ParticipationComponents = props => {
  const {
    context,
    isloadingCountriesSelector,
    iserrorCountriesSelector,
    isloadingParticipationSelector,
    iserrorParticipationSelector,
    isloadingChampSelector,
    iserrorChampSelector,
    participationInfo,
    participatingCountries,
    studyChampions,
    onGetChampions,
    onGetParticipation,
    onGetCountry,
    protocolNumber,
    protocolId,
    saveParticipantCountriesData,
    saveParticipantInfo,
    saveRestrictionInfo,
    syncParticipants,
    isLoadingPrtDetails,
    iserrorPrtDetails
  } = props;

  const [pspObjId, setPspObjId] = useState(null);
  const [restrictionFlag, setRestrictionFlag] = useState(true);
  const [participationIndex, setParticipationIndex] = useState({highlightRow: null});

  useEffect(() => {
    if (!_.isEmpty(protocolNumber)) {
      bindLogger(onGetParticipation, context)(protocolNumber);
      bindLogger(onGetChampions, context)(protocolNumber);
      bindLogger(onGetCountry, context)(protocolNumber);
    }
  }, [protocolNumber, onGetParticipation, onGetChampions,
    onGetCountry, context]);

  const clickedRow = (row, index) => {
    setPspObjId(row.pspId);
    setParticipationIndex({highlightRow: index});
  }

  const participationDataLoad = ({data}) => {
    if(data.length>0 && restrictionFlag){
      setPspObjId(data[0].pspId);
      setParticipationIndex({highlightRow: 0});
    }

    if(!restrictionFlag){
      setRestrictionFlag(true)
    }
  }

  const pspObj = _.find(participationInfo.data, ['pspId', pspObjId]);
   
  const { restrictions, hasRestrictions, pspId, esysStatus } = pspObj || {};
  return (
    <RenderControl
      isLoading={isLoadingPrtDetails}
      iserror={iserrorPrtDetails}
      compName="Participation Information"
      permission
    >
      <Grid container justify="center" spacing={4}>
        <Grid item xs={8}>
          <RenderControl
            isLoading={isloadingCountriesSelector}
            iserror={iserrorCountriesSelector}
            compName="IND/Countries"
            permission={CanUserViewCountries()}
          >
            <ParticipationCountries
              protocolId={protocolId}
              participationInfo={participationInfo.data}
              participatingCountries={participatingCountries.data}
              onSave={saveParticipantCountriesData}
              CanUserEditCountries={CanUserEditCountries()}
            />
          </RenderControl>
        </Grid>
        <Grid item xs={4}>
          <RenderElement access>
            <div> {/* <div className="vertical-divider-left"> */}
              <Typography primary header style={{ textAlign: 'left', fontSize: '1.4rem !important' }}>
                Site Participation Test
              </Typography>
              <SiteRegistrationTest
                protocolNumber={protocolNumber}
              />
            </div>
          </RenderElement>
        </Grid>
        <Grid item xs={12}>
          <RenderControl
            isLoading={isloadingParticipationSelector}
            iserror={iserrorParticipationSelector}
            compName="Participation"
            permission={CanUserViewParticipation()}
          >
            <Participation
              participationInfo={participationInfo.data}
              participationIndex={participationIndex}
              setParticipationIndex={setParticipationIndex}
              participationDataLoad={participationDataLoad}
              onSave={saveParticipantInfo}
              onRowClicked={clickedRow}
              syncParticipants={syncParticipants}
              CanUserEditParticipation={CanUserEditParticipation()}
            />
          </RenderControl>
        </Grid>
        <Grid item xs={12}>
          <RenderControl
            isLoading={isloadingParticipationSelector}
            iserror={iserrorParticipationSelector}
            compName="Restriction For"
            permission={CanUserViewParticipation()}
          >
            <RenderDelay delayTimeInSec={1} elValue={pspObj}>
              <RestrictionFor
                restrictions={restrictions}
                hasRestrictions={hasRestrictions}
                onSave={saveRestrictionInfo}
                pspObj={pspObj}
                setRestrictionFlag={setRestrictionFlag}
                pspId={pspId}
                canUserEdit={CanUserEditRestrictions() && esysStatus === 'Active'}
              />
            </RenderDelay>
          </RenderControl>
        </Grid>
        <Grid item xs={12}>
          <RenderControl
            isLoading={isloadingChampSelector}
            iserror={iserrorChampSelector}
            compName="Study Champions"
            permission
          >
            <StudyChampions data={studyChampions.data} />
          </RenderControl>
        </Grid>
      </Grid>
    </RenderControl>
  );
};

const withConnectState = connect(
  (state, ownProps) => ({
    protocolId: ProtocolIdSelector(state),
    protocolNumber: ProtocolNumSelector(state),
    participationInfo: state.participation.participationInfo,
    participatingCountries: state.participation.countries,
    studyChampions: state.participation.studyChampions,
    pspId: state.participation.selectedParticipant,
    isloadingCountriesSelector: loadingCountriesSelector(state),
    iserrorCountriesSelector: errorCountriesSelector(state),
    isloadingParticipationSelector: loadingParticipationSelector(state),
    iserrorParticipationSelector: errorParticipationSelector(state),
    isloadingChampSelector: loadingChampSelector(state),
    iserrorChampSelector: errorChampSelector(state),
    isLoadingPrtDetails: lSelectorGetProtDetails(state),
    iserrorPrtDetails: eSelectorGetProtDetails(state)
  }),
  dispatch => bindActionCreators(
    {
      onGetParticipation: getParticipation,
      onSaveParticipation: saveParticipation,
      onGetChampions: getChampions,
      onGetCountry: getCountry,
      onSaveRestriction: saveRestriction,
      onParticipantsSync: setParticipantsSync
    },
    dispatch
  )
);

const ParticipationContainer = compose(
  withAppContext,
  withConnectState,
  withHandlers({
    saveParticipantInfo: props => data => {
      const { onSaveParticipation, context, protocolId } = props;
      return bindLogger(onSaveParticipation, context)(protocolId, {
        ...data, prtId: protocolId
      });
    },
    saveRestrictionInfo: props => (data, pspId) => {
      const {
        onSaveRestriction, context, protocolId
      } = props;
      return bindLogger(onSaveRestriction, context)(protocolId, pspId, {
        ...data, prtId: protocolId, pspId
      });
    },
    syncParticipants: props => () => {
      const { protocolNumber, onParticipantsSync, context } = props;
      return bindLogger(onParticipantsSync, context)(protocolNumber);
    }
  })
)(ParticipationComponents);

export default ParticipationContainer;
